/**
 * Left-chrome widths, in px.
 *
 * A leaf module because several surfaces need these and importing them from
 * `NavRail` would close a cycle (`NavRail` imports
 * `NavRailFooter`, which needs the rail width).
 *
 * These must agree with what is actually rendered or the content margin desyncs
 * from the chrome — a gap or a clipped panel, with no compiler help. Consumers:
 *   NAV_RAIL_WIDTH             — the rail's box, Layout's content margin,
 *                                LeftPanelColumn's offset and overlay width,
 *                                and the checklist panel's left offset
 *   *_MIN_WIDTH / *_MAX_WIDTH  — each panel's `usePanelResize` bounds, and the
 *                                initial value of Layout's per-panel width state
 */
/**
 * 88. The number is set by the GUTTER, not by the labels: the rail is the only
 * chrome whose left neighbour is the window edge and whose right neighbour is
 * the content canvas, which is flush against it (`Layout` gives the canvas no
 * left inset, because the rail's own box is the frame on that side). So the
 * space either side of a rail target is half of what the target leaves, and
 * it is the same on both sides only for as long as nothing is inset between
 * them.
 *
 * The targets are {@link NAV_RAIL_TARGET_SIZE} squares, so 88 leaves 18px
 * either side of them, and the widest label ("Workspace", ~57px at 11px) keeps
 * ~15px either side (David, 2026-10-31). 84 is too narrow: with the old 64px
 * targets it left 10px, and the labels read as squashed against the canvas
 * edge (reported 2026-09). An earlier fix insetting the canvas instead was
 * wrong for the same arithmetic: it widens the RIGHT gutter alone.
 *
 * `NavRailItem.test.tsx` pins the 64px label column against this constant, so
 * a shrink fails there rather than silently truncating "Workspace".
 */
export const NAV_RAIL_WIDTH = 88;
/**
 * MINIMUM width of the Bower chat panel, in px.
 *
 * It shares ONE column (`LeftPanelColumn`) with the workspace sidebar. The chat used to size itself from the VIEWPORT instead
 * — `max(400, innerWidth * 0.4)`, so ~576px at 1440 — which meant switching
 * between Workspace and Chat moved the column by a couple of hundred pixels
 * and shoved the page content with it. That reads as the page lurching, not as
 * a panel changing.
 *
 * The transcript-readability tension that made 352 a poor DEFAULT is answered
 * by {@link PANEL_DEFAULT_WIDTH} rather than by raising this floor: a user who
 * wants a narrow panel should still be able to drag down to 352.
 */
export const BIRD_PANEL_MIN_WIDTH = 352;

/**
 * How much of the viewport the Bower panel may take at its widest, as a
 * fraction — the sidebar has a fixed px ceiling, the chat's scales because a transcript on a 2560px display should be able to use more
 * of it than one on a 1280px laptop.
 *
 * Shared rather than inlined because the stored width is clamped against it by
 * BOTH the panel and the container that paints the column around the panel
 * (`useBirdPanel`), and a ceiling the two disagree on is a column painted at a
 * width the panel never renders.
 */
export const BIRD_PANEL_MAX_VIEWPORT_FRACTION = 0.5;

/**
 * How wide the left panel OPENS, in px, for whichever panel holds the column.
 *
 * Separate from the floor above, which is the whole point. The floors
 * bound what a user may drag to; this is only the starting point for someone
 * who has never dragged at all. Collapsing the two — which is what the code did
 * until 2026-09, every call site passing its `*_MIN_WIDTH` as its
 * `defaultWidth` — means widening the default also takes away the narrow end of
 * everyone's drag range.
 *
 * 420, up from 352. At 352 a chat transcript is cramped, which
 * `LeftPanelColumn`'s overlay comment already argued about row lists versus
 * transcripts, and the composer's action row had no slack at all.
 *
 * A dragged width WINS over this and persists per panel (`usePanelResize`
 * writes `workspaceSidebarWidth`, and `BirdChatPanel` writes `birdPanelWidth`), so changing this moves first-run only — anyone who
 * has already sized a panel keeps their width.
 *
 * NOTE it does not reach the composer's label threshold. `ChatComposer`
 * collapses "Add context" and "Sources" to icons below a 420px CONTAINER, and
 * the container is roughly this minus 58px of card gutter and padding — so at
 * 420 the panel's composer still shows icons. Restoring the words needs ~480,
 * which is wide enough to be a real bite out of the page; the collapse is the
 * better answer at panel widths, and it is what stops the row wrapping.
 */
export const PANEL_DEFAULT_WIDTH = 420;

/**
 * The workspace sidebar (`WorkspaceSidebar`). The approved mockup draws it at
 * 272px, so it opens there rather than at {@link PANEL_DEFAULT_WIDTH}: at 420
 * a 1440px window left the canvas too narrow for its top bar and filter row.
 *
 * Its own range and its own stored key, not the retired drawer's
 * (`workspaceDrawerWidth`), whose 352px floor would have made the mockup
 * width unreachable. The floor keeps a tree row's name readable past its
 * indent, glyph and menu.
 */
export const WORKSPACE_SIDEBAR_DEFAULT_WIDTH = 272;
export const WORKSPACE_SIDEBAR_MIN_WIDTH = 240;
export const WORKSPACE_SIDEBAR_MAX_WIDTH = 480;
/** Where `usePanelResize` persists the sidebar's dragged width. */
export const WORKSPACE_SIDEBAR_WIDTH_KEY = 'workspaceSidebarWidth';

/**
 * Edge of the rail's filled targets, all one square with the `lg` radius: the
 * workspace tile, the Create button, and the active/hover chip behind each
 * `NavRailItem` icon, so a lit item reads as the same shape as Create above it
 * (a smaller 44x32 chip looked like a different control, reported 2026-10).
 * Square because the tile at the top is a logo, which a 64px-wide target could
 * only pad or crop (David, 2026-10-31).
 */
export const NAV_RAIL_TARGET_SIZE = 52;
